import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { motion } from "framer-motion";
import {
  Check,
  CheckCircle2,
  ChevronRight,
  Copy,
  Download,
  ShieldCheck,
  Sparkles,
  Zap,
} from "lucide-react";

export default function ExtensionPage() {
  const [copiedUrl, setCopiedUrl] = useState(false);

  const handleCopyUrl = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", py: { xs: 4, md: 8 } }}>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box textAlign="center" mb={{ xs: 6, md: 8 }}>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Chip
              icon={<Sparkles size={16} color="#2563eb" />}
              label="ChordMaster Chrome Extension v1.0 • Manifest V3"
              sx={{
                bgcolor: "#eff6ff",
                color: "#1d4ed8",
                fontWeight: 700,
                fontSize: "0.85rem",
                px: 1,
                py: 2.2,
                borderRadius: 9999,
                border: "1px solid #bfdbfe",
                mb: 3,
                boxShadow: "0 2px 8px rgba(37, 99, 235, 0.08)",
              }}
            />

            <Typography
              variant="h2"
              component="h1"
              fontWeight="900"
              sx={{
                fontSize: { xs: "2.25rem", sm: "3.25rem", md: "3.75rem" },
                letterSpacing: "-1.5px",
                lineHeight: 1.15,
                color: "#0f172a",
                mb: 2.5,
              }}
            >
              Open Any Tab in ChordMaster with{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  display: "inline",
                }}
              >
                1 Click
              </Box>
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: "#64748b",
                maxWidth: 720,
                mx: "auto",
                fontWeight: 400,
                fontSize: { xs: "1rem", sm: "1.2rem" },
                lineHeight: 1.6,
                mb: 4,
              }}
            >
              Browse Ultimate Guitar or Tab4U as usual. Our browser extension automatically detects song pages and injects a sleek floating widget to launch ChordMaster's distraction-free interactive viewer.
            </Typography>

            {/* CTA Buttons */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="center"
              alignItems="center"
            >
              <Button
                variant="contained"
                size="large"
                href="/chordmaster-extension.zip"
                download="chordmaster-extension.zip"
                startIcon={<Download size={20} />}
                sx={{
                  py: 1.75,
                  px: 4,
                  fontSize: "1.05rem",
                  fontWeight: 700,
                  borderRadius: 3,
                  textTransform: "none",
                  background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                  boxShadow: "0 8px 25px rgba(37, 99, 235, 0.35)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
                    boxShadow: "0 10px 30px rgba(37, 99, 235, 0.45)",
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                Download Extension (.zip)
              </Button>

              <Button
                variant="outlined"
                size="large"
                onClick={() => {
                  const el = document.getElementById("install-instructions");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                endIcon={<ChevronRight size={18} />}
                sx={{
                  py: 1.75,
                  px: 3.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                  borderRadius: 3,
                  textTransform: "none",
                  borderColor: "#cbd5e1",
                  color: "#334155",
                  bgcolor: "#ffffff",
                  "&:hover": {
                    borderColor: "#94a3b8",
                    bgcolor: "#f1f5f9",
                  },
                }}
              >
                Installation Steps
              </Button>
            </Stack>

            {/* Compatibility badges */}
            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              flexWrap="wrap"
              sx={{ mt: 4, gap: 1 }}
            >
              <Chip
                icon={<ShieldCheck size={16} color="#059669" />}
                label="Chrome, Brave, Edge & Opera"
                size="small"
                sx={{ bgcolor: "#ecfdf5", color: "#065f46", fontWeight: 600 }}
              />
              <Chip
                icon={<Zap size={16} color="#7c3aed" />}
                label="Zero Latency • Manifest V3"
                size="small"
                sx={{ bgcolor: "#f5f3ff", color: "#5b21b6", fontWeight: 600 }}
              />
              <Chip
                icon={<CheckCircle2 size={16} color="#2563eb" />}
                label="100% Free & Open Source"
                size="small"
                sx={{ bgcolor: "#eff6ff", color: "#1e40af", fontWeight: 600 }}
              />
            </Stack>
          </motion.div>
        </Box>

        {/* Step-by-Step Installation Guide */}
        <Box id="install-instructions" mb={4}>
          <Box textAlign="center" mb={6}>
            <Typography variant="overline" color="primary" fontWeight="bold" letterSpacing={1.2}>
              EASY 4-STEP SETUP
            </Typography>
            <Typography variant="h4" fontWeight="800" color="#0f172a">
              How to Install via Chrome "Load unpacked"
            </Typography>
            <Typography variant="body1" color="text.secondary" maxWidth={600} mx="auto" mt={1}>
              Install directly into Google Chrome, Brave, Microsoft Edge, or Arc in under 60 seconds without needing the Chrome Web Store.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {/* Step 1 */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                variant="outlined"
                sx={{
                  p: 3.5,
                  height: "100%",
                  borderRadius: 3,
                  bgcolor: "#ffffff",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      bgcolor: "#eff6ff",
                      color: "#2563eb",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 800,
                      fontSize: "1.1rem",
                    }}
                  >
                    1
                  </Box>
                  <Typography variant="h6" fontWeight="bold">
                    Download & Extract ZIP
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" mb={3} flex={1}>
                  Click the download button to get the extension archive <code>chordmaster-extension.zip</code>. Once downloaded, right-click and <strong>Extract All</strong> to an easily accessible folder.
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Download size={16} />}
                  href="/chordmaster-extension.zip"
                  download="chordmaster-extension.zip"
                  sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
                >
                  Download chordmaster-extension.zip
                </Button>
              </Card>
            </Grid>

            {/* Step 2 */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                variant="outlined"
                sx={{
                  p: 3.5,
                  height: "100%",
                  borderRadius: 3,
                  bgcolor: "#ffffff",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      bgcolor: "#eff6ff",
                      color: "#2563eb",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 800,
                      fontSize: "1.1rem",
                    }}
                  >
                    2
                  </Box>
                  <Typography variant="h6" fontWeight="bold">
                    Open Extensions Page
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" mb={3} flex={1}>
                  In your browser's address bar, type <code>chrome://extensions</code> (or <code>edge://extensions</code> or <code>brave://extensions</code>) and hit <strong>Enter</strong>.
                </Typography>
                <Box
                  sx={{
                    bgcolor: "#f1f5f9",
                    p: 1.5,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="body2" fontFamily="monospace" color="#334155" fontWeight="bold">
                    chrome://extensions
                  </Typography>
                  <Button
                    size="small"
                    startIcon={copiedUrl ? <Check size={14} /> : <Copy size={14} />}
                    onClick={() => handleCopyUrl("chrome://extensions")}
                    sx={{ textTransform: "none", fontSize: "0.75rem" }}
                  >
                    {copiedUrl ? "Copied" : "Copy"}
                  </Button>
                </Box>
              </Card>
            </Grid>

            {/* Step 3 */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                variant="outlined"
                sx={{
                  p: 3.5,
                  height: "100%",
                  borderRadius: 3,
                  bgcolor: "#ffffff",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      bgcolor: "#eff6ff",
                      color: "#2563eb",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 800,
                      fontSize: "1.1rem",
                    }}
                  >
                    3
                  </Box>
                  <Typography variant="h6" fontWeight="bold">
                    Enable Developer Mode
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" mb={2} flex={1}>
                  In the top-right corner of the Extensions page, toggle the <strong>Developer mode</strong> switch to <strong>ON</strong>.
                </Typography>
                <Box
                  sx={{
                    bgcolor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    p: 2,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Zap size={20} color="#2563eb" />
                  <Typography variant="caption" color="text.secondary">
                    Enabling developer mode unlocks the "Load unpacked" button so you can install local extensions.
                  </Typography>
                </Box>
              </Card>
            </Grid>

            {/* Step 4 */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                variant="outlined"
                sx={{
                  p: 3.5,
                  height: "100%",
                  borderRadius: 3,
                  bgcolor: "#ffffff",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      bgcolor: "#eff6ff",
                      color: "#2563eb",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 800,
                      fontSize: "1.1rem",
                    }}
                  >
                    4
                  </Box>
                  <Typography variant="h6" fontWeight="bold">
                    Click "Load unpacked"
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" mb={2} flex={1}>
                  Click the <strong>Load unpacked</strong> button in the top-left toolbar, then select the extracted folder (containing <code>manifest.json</code>).
                </Typography>
                <Box
                  sx={{
                    bgcolor: "#ecfdf5",
                    border: "1px solid #a7f3d0",
                    p: 1.5,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <CheckCircle2 size={18} color="#059669" />
                  <Typography variant="caption" color="#065f46" fontWeight="bold">
                    You're ready! Visit Tab4U or Ultimate Guitar to see the floating widget.
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}
